import type { AssessmentResult, RuleData } from "./types";

const STATE_TAX_BREAKPOINT_2026 = 660400;

export type EligibilityInput = {
  inEligibleArea: "yes" | "no" | "unknown";
  householdType: string;
  householdIncome: number;
  paysStateTax: "yes" | "no";
  recentEvOrHybrid: "yes" | "no";
  acquisition: "purchase" | "leasing";
  vehicleCost: number;
  ageAndIdOk: "yes" | "no";
  hasSwedishPersonalNumber: "yes" | "no";
  protectedRegistration: "yes" | "no";
  debtsOrOwnershipIssues: "yes" | "no";
  carIsElectricClass?: "yes" | "no";
  carIsRegisteredActive?: "yes" | "no";
  carNoPreviousPremium?: "yes" | "no";
  registeredOwnerOrHolder?: "yes" | "no";
  notOwnedByHouseholdRelatedCompany?: "yes" | "no";
  notBusinessUse?: "yes" | "no";
  applicationBeforeDeadline?: "yes" | "no";
  recentRegistrationOk?: "yes" | "no";
  noOtherOngoingHouseholdCase?: "yes" | "no";
  firstPremiumForApplicant?: "yes" | "no";
  sameAddressFourMonths?: "yes" | "no";
  eligibleAreaTwelveMonths?: "yes" | "no";
  registeredForTwelveMonths?: "yes" | "no";
  truthfulApplication?: "yes" | "no";
  canProvideDocuments?: "yes" | "no";
  acceptEuControls?: "yes" | "no";
};

export function assessEligibility(input: EligibilityInput, rules: RuleData): AssessmentResult {
  const blockers: string[] = [];
  const cautions: string[] = [];
  const ok: string[] = [];

  if (input.inEligibleArea === "no") blockers.push("Bostaden verkar inte ligga i ett stödberättigat område.");
  if (input.inEligibleArea === "unknown") cautions.push("Området behöver kontrolleras mot Naturvårdsverkets officiella test.");
  if (input.inEligibleArea === "yes") ok.push("Du anger att bostaden ligger i ett stödberättigat område.");

  if (input.paysStateTax === "yes") blockers.push("Ingen vuxen i hushållet får ha inkomst som leder till statlig inkomstskatt.");

  const incomeLimit = rules.incomeThresholds[input.householdType];
  if (!Number.isFinite(input.householdIncome) || input.householdIncome <= 0) {
    cautions.push("Hushållsinkomst saknas och behöver fyllas i eller kontrolleras.");
  } else if (incomeLimit && input.householdIncome >= incomeLimit) {
    blockers.push(`Hushållsinkomsten är inte under riktvärdet ${formatMoney(incomeLimit)} för vald hushållstyp.`);
  } else if (incomeLimit) {
    ok.push(`Hushållsinkomsten ligger under riktvärdet ${formatMoney(incomeLimit)} för vald hushållstyp.`);
  } else {
    cautions.push("Vald hushållstyp saknar lokalt riktvärde och behöver kontrolleras.");
  }

  const stateTaxEstimate = estimateStateTaxRisk(input.householdType, input.householdIncome);
  if (input.paysStateTax === "no" && stateTaxEstimate.status === "likely-over") {
    cautions.push(`Kan stoppa ansökan: ${stateTaxEstimate.message}`);
  } else if (input.paysStateTax === "no" && stateTaxEstimate.status === "margin") {
    cautions.push(`Kan stoppa ansökan: ${stateTaxEstimate.message}`);
  } else if (input.paysStateTax === "no" && stateTaxEstimate.status === "likely-under") {
    ok.push(stateTaxEstimate.message);
  }

  if (input.recentEvOrHybrid === "yes") blockers.push("Hushållet får inte ha ägt eller leasat elbil eller laddhybrid de senaste 12 månaderna.");

  if (input.acquisition === "purchase") {
    if (!Number.isFinite(input.vehicleCost) || input.vehicleCost <= 0) {
      cautions.push("Köppris saknas och behöver kontrolleras mot prisintervallet.");
    } else if (input.vehicleCost < rules.purchase.minPrice || input.vehicleCost > rules.purchase.maxPrice) {
      blockers.push(`Köppriset behöver vara ${formatMoney(rules.purchase.minPrice)}-${formatMoney(rules.purchase.maxPrice)}.`);
    } else {
      ok.push("Köppriset ligger inom Naturvårdsverkets angivna intervall.");
    }
  } else {
    if (!Number.isFinite(input.vehicleCost) || input.vehicleCost <= 0) {
      cautions.push("Leasingkostnad saknas och behöver kontrolleras mot månadsintervallet.");
    } else if (input.vehicleCost < rules.leasing.minMonthlyCost || input.vehicleCost > rules.leasing.maxMonthlyCost) {
      blockers.push(`Leasingkostnaden behöver vara ${formatMoney(rules.leasing.minMonthlyCost)}-${formatMoney(rules.leasing.maxMonthlyCost)} per månad.`);
    } else {
      ok.push("Leasingkostnaden ligger inom Naturvårdsverkets angivna intervall.");
    }
  }

  if (input.ageAndIdOk === "no") blockers.push("Sökanden måste bland annat vara minst 18 år och kunna behandlas i myndighetens ansökningsflöde.");
  if (input.hasSwedishPersonalNumber === "no") blockers.push("Naturvårdsverket anger att ansökningar utan svenskt personnummer avvisas.");
  if (input.protectedRegistration === "yes") blockers.push("Naturvårdsverket anger att ansökningar med skyddad folkbokföring avvisas.");
  if (input.debtsOrOwnershipIssues === "yes") cautions.push("Fordonsrelaterade skulder, ägande och innehav kontrolleras av myndigheten.");

  const advancedBlockers: Array<[keyof EligibilityInput, string]> = [
    ["carIsElectricClass", "Bilen behöver vara en elbil enligt förordningens definition."],
    ["carIsRegisteredActive", "Bilen behöver vara påställd och utan körförbud."],
    ["carNoPreviousPremium", "Bilen får inte tidigare ha legat till grund för elbilspremie i hushållet."],
    ["registeredOwnerOrHolder", "Sökanden behöver vara registrerad ägare eller innehavare."],
    ["notOwnedByHouseholdRelatedCompany", "Bilen får inte tidigare ha ägts av hushåll, närstående eller närstående företag enligt 15 §."],
    ["notBusinessUse", "Bilen får inte vara avsedd att användas i näringsverksamhet."],
    ["applicationBeforeDeadline", "Ansökan behöver komma in senast 30 juni 2029."],
    ["recentRegistrationOk", "Om bilen redan är köpt/leasad behöver registreringen ligga inom tvåmånadersregeln."],
    ["noOtherOngoingHouseholdCase", "Ingen i hushållet får ha ett annat pågående ärende enligt förordningen."],
    ["firstPremiumForApplicant", "Elbilspremie får bara lämnas en gång per sökande."],
    ["sameAddressFourMonths", "Beslutet kan kräva samma stödberättigade adress i minst fyra månader."],
    ["eligibleAreaTwelveMonths", "Beslutet kan kräva bosättning i stödberättigat område i minst tolv månader."],
    ["registeredForTwelveMonths", "Starttillägg kan behöva återbetalas delvis om ägar-/innehavarregistrering inte hålls i tolv månader."],
    ["truthfulApplication", "Ansökan kräver försäkran på heder och samvete."],
    ["canProvideDocuments", "Sökanden behöver kunna lämna handlingar eller uppgifter som Naturvårdsverket begär."],
    ["acceptEuControls", "Beslut kan innehålla kontrollvillkor enligt förordningen."]
  ];
  for (const [key, message] of advancedBlockers) {
    if (input[key] === "no") blockers.push(message);
  }

  if (blockers.length > 0) {
    return { status: "troligen-nej", title: "Troligen nej", reasons: blockers, cautions };
  }

  if (cautions.length > 0) {
    return { status: "kontrollera", title: "Behöver kontrolleras", reasons: ok, cautions };
  }

  return {
    status: "troligen-ja",
    title: "Troligen ja",
    reasons: ok,
    cautions: ["Detta är ingen myndighetsprövning. Beslut fattas av Naturvårdsverket efter kontroll mot register och ansökningshandlingar."]
  };
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 }).format(value);
}

function estimateStateTaxRisk(householdType: string, householdIncome: number): { status: "likely-over" | "margin" | "likely-under" | "unknown"; message: string } {
  if (!Number.isFinite(householdIncome) || householdIncome <= 0) {
    return { status: "unknown", message: "Statlig inkomstskatt kan inte uppskattas utan hushållsinkomst." };
  }

  const adults = householdType.toLowerCase().startsWith("två") ? 2 : 1;
  if (adults === 1) {
    if (householdIncome > STATE_TAX_BREAKPOINT_2026) {
      return {
        status: "likely-over",
        message: `Med en vuxen antas inkomsten ligga på samma person. ${formatMoney(householdIncome)} är över Skatteverkets brytpunkt för statlig inkomstskatt 2026 (${formatMoney(STATE_TAX_BREAKPOINT_2026)}), vilket talar för att villkoret inte uppfylls.`
      };
    }
    return {
      status: "likely-under",
      message: `Med en vuxen ligger inkomsten under Skatteverkets brytpunkt för statlig inkomstskatt 2026 (${formatMoney(STATE_TAX_BREAKPOINT_2026)}).`
    };
  }

  const equalShare = householdIncome / 2;
  const higherEarnerMarginShare = householdIncome * 0.7;
  if (equalShare > STATE_TAX_BREAKPOINT_2026) {
    return {
      status: "likely-over",
      message: `Även vid jämn fördelning mellan två vuxna blir inkomsten cirka ${formatMoney(equalShare)} per vuxen, över brytpunkten för statlig inkomstskatt 2026 (${formatMoney(STATE_TAX_BREAKPOINT_2026)}).`
    };
  }
  if (higherEarnerMarginShare > STATE_TAX_BREAKPOINT_2026) {
    return {
      status: "margin",
      message: `Risk för statlig inkomstskatt: vid två vuxna antar appen först ungefär jämn inkomstfördelning, men testar också ett 70/30-scenario eftersom en vuxen ofta kan tjäna betydligt mer. Om en vuxen står för cirka 70 % av hushållsinkomsten blir det ${formatMoney(higherEarnerMarginShare)}, vilket är över brytpunkten ${formatMoney(STATE_TAX_BREAKPOINT_2026)}. Kontrollera därför skattebeskedet.`
    };
  }
  return {
    status: "likely-under",
    message: `Vid ungefär jämn fördelning mellan två vuxna ligger inkomsten under brytpunkten för statlig inkomstskatt 2026 (${formatMoney(STATE_TAX_BREAKPOINT_2026)}).`
  };
}
